/**
 * Routes for ADM-1..9.
 *
 * The whole tree sits behind `RoleGuard role="admin"` from @most/auth. Client-side guards are
 * UX only — the backend enforces authorization on every request, and a role mismatch renders an
 * in-app 403 rather than revealing whether the route exists (docs/06 §3).
 *
 * The router has no basename: since D16 this SPA is served from the root of its own subdomain
 * (adm.mostholding.org), matching Vite's `base: '/'`.
 */
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RoleGuard } from '@most/auth';
import { AdminShell } from '@/components/shell/AdminShell';
import { TableSkeleton } from '@/components/common/States';
import {
  AccountUnavailable,
  Forbidden,
  GuardLoading,
  MustChangePassword,
} from '@/components/common/Interstitials';
import { NotFoundPage } from './NotFoundPage';

// Route-level code splitting (docs/06 §11). The pricing calculator and its decimal math only
// ship when Merdan actually opens the pricing queue.
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ApprovalsPage = lazy(() =>
  import('@/features/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })),
);
const PricingPage = lazy(() =>
  import('@/features/pricing/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const OrdersPage = lazy(() =>
  import('@/features/orders/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetailPage = lazy(() =>
  import('@/features/orders/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
);
const UsersPage = lazy(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const UserDetailPage = lazy(() =>
  import('@/features/users/UserDetailPage').then((m) => ({ default: m.UserDetailPage })),
);
const CategoriesPage = lazy(() =>
  import('@/features/categories/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const InboxPage = lazy(() =>
  import('@/features/inbox/InboxPage').then((m) => ({ default: m.InboxPage })),
);
const AuditPage = lazy(() =>
  import('@/features/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const ProfilePage = lazy(() =>
  import('@/features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const MonitoringTab = lazy(() =>
  import('@/features/settings/MonitoringTab').then((m) => ({ default: m.MonitoringTab })),
);
const RolesTab = lazy(() =>
  import('@/features/settings/RolesTab').then((m) => ({ default: m.RolesTab })),
);

export function AppRouter() {
  return (
    // Every non-allow state gets a REAL screen. RoleGuard's props default to null, and a
    // null interstitial is a blank page — which is exactly what a buyer with a shared
    // localhost cookie used to see when they opened /admin.
    <RoleGuard
      role="admin"
      fallback={<GuardLoading />}
      forbidden={<Forbidden />}
      blocked={<AccountUnavailable />}
      mustChangePassword={<MustChangePassword />}
    >
      <Routes>
        <Route element={<AdminShell />}>
          <Route
            path="/"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route
            path="/approvals"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <ApprovalsPage />
              </Suspense>
            }
          />
          <Route
            path="/pricing"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <PricingPage />
              </Suspense>
            }
          />
          <Route
            path="/orders"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <OrdersPage />
              </Suspense>
            }
          />
          {/*
           * Orders route by NUMBER (M-2026-0042), not by UUID — human-readable, unique, and the
           * same convention the contract's admin order endpoints use.
           */}
          <Route
            path="/orders/:number"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <OrderDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/users"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <UsersPage />
              </Suspense>
            }
          />
          <Route
            path="/users/:id"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <UserDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/categories"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <CategoriesPage />
              </Suspense>
            }
          />
          <Route
            path="/inbox"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <InboxPage />
              </Suspense>
            }
          />
          <Route
            path="/notifications"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <NotificationsPage />
              </Suspense>
            }
          />
          {/* Audit MOVED under Settings (ADM-10). The old top-level path redirects so the command
              palette, bookmarks and the dashboard's "view all" link keep working. */}
          <Route path="/audit" element={<Navigate to="/settings/audit" replace />} />

          {/* Settings — Monitoring, Roles & Permissions, and the (relocated) Audit log. The shell
              renders the tab bar; the index redirects to the first tab so /settings is never blank. */}
          <Route
            path="/settings"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <SettingsPage />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="/settings/monitoring" replace />} />
            <Route
              path="monitoring"
              element={
                <Suspense fallback={<TableSkeleton />}>
                  <MonitoringTab />
                </Suspense>
              }
            />
            <Route
              path="roles"
              element={
                <Suspense fallback={<TableSkeleton />}>
                  <RolesTab />
                </Suspense>
              }
            />
            <Route
              path="audit"
              element={
                <Suspense fallback={<TableSkeleton />}>
                  <AuditPage />
                </Suspense>
              }
            />
          </Route>

          {/* The admin's OWN account (language, password, Telegram). Reached from the account
              menu in the top bar — it is not a queue, so it has no rail entry. */}
          <Route
            path="/profile"
            element={
              <Suspense fallback={<TableSkeleton />}>
                <ProfilePage />
              </Suspense>
            }
          />

          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </RoleGuard>
  );
}
